import { useEffect } from 'react';
import { type MediaHandlerProps, useMediaUtils } from '..';

type PlaybackUpdate = {
  type: 'playback_update';
  payload: {
    duration: number;
    isBuffering: boolean;
    isPaused: boolean;
    position: number;
  };
};

type PlaybackReady = {
  type: 'ready';
};

type SpotifyMessage = PlaybackUpdate | PlaybackReady;

export const useSpotifyHandler = ({ eventName }: MediaHandlerProps) => {
  const { isFirstPlay, push, millisecondsToSeconds, getPercentage, playbackDuration } = useMediaUtils(eventName);

  const playbackUpdate = ({ duration, isBuffering, isPaused, position }: PlaybackUpdate['payload']) => {
    // track if its the first play
    if (!isFirstPlay.current && isPaused === false && isBuffering === true && position === 0 && duration === 0) {
      isFirstPlay.current = true;
      push('play');
    }

    if (duration > 0 && playbackDuration.current === 0) {
      // track when we have the duration of the track
      // convert duration from milliseconds to seconds rounded to nearest second
      playbackDuration.current = millisecondsToSeconds(duration);

      // fallback in case the first play was not detected
      if (!isFirstPlay.current) {
        isFirstPlay.current = true;
        push('play');
      }
    }

    if (isPaused === false && duration > 0 && position > 0) {
      // track playback progress
      const percentage = getPercentage(position, duration);
      if (!!percentage) push(percentage);
    }
  };

  useEffect(() => {
    const listener = (event: MessageEvent<SpotifyMessage>) => {
      if (!event.origin.includes('.spotify.com')) return;
      const message = event.data as SpotifyMessage;
      if (message.type === 'playback_update') playbackUpdate(message.payload);
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, []);
};
